import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, map, shareReplay, switchMap, of, combineLatest, take } from 'rxjs';
import { MockAuthService } from './mock-auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly mockAuthService = inject(MockAuthService);

  // Determine if we're using mock auth
  private get isLocalDev(): boolean {
    return !!(environment as any).isLocalDev || !(environment as any).auth?.enabled;
  }

  // Observable that emits authentication state changes
  readonly isAuthenticated$ = this.isLocalDev
    ? this.mockAuthService.isAuthenticated$.pipe(map((isAuth) => ({ isAuthenticated: isAuth })))
    : this.oidcSecurityService.isAuthenticated$;

  // Observable that emits user data
  readonly userData$ = this.isLocalDev ? this.mockAuthService.userData$ : this.oidcSecurityService.userData$;

  // Observable that emits access token
  readonly accessToken$ = this.isLocalDev
    ? this.mockAuthService.getAccessToken()
    : this.oidcSecurityService.getAccessToken();

  // Observable that emits ID token
  readonly idToken$ = this.isLocalDev ? this.mockAuthService.getIdToken() : this.oidcSecurityService.getIdToken();

  // Observable that emits the current tenant ID
  readonly tenantId$ = this.getUserClaims().pipe(
    map((claims) => {
      if (!claims) return null;
      // Check multiple possible claim names for tenant ID
      return claims.tenant_id || claims.tenantId || claims['custom:tenant-id'] || null;
    }),
    shareReplay(1)
  );

  constructor() {
    // Log authentication status changes
    this.isAuthenticated$.subscribe((authenticated) => {
      console.log('🔐 Auth Service - Authentication status changed:', authenticated);
      console.log('🔐 Auth Service - Using mock auth:', this.isLocalDev);
    });

    // Log tenant ID changes
    this.tenantId$.subscribe((tenantId) => {
      console.log('🏢 Auth Service - Tenant ID changed:', tenantId);
    });

    // Auto-login in local development mode
    if (this.isLocalDev) {
      console.log('🎭 Auth Service - Local development mode detected');
      setTimeout(() => {
        this.mockAuthService.autoLogin();
      }, 100);
    }
  }

  /**
   * Initiates the login process
   */
  login(): void {
    if (this.isLocalDev) {
      console.log('🎭 Auth Service - Starting mock login');
      this.mockAuthService.login().subscribe({
        next: (result) => console.log('🎭 Auth Service - Mock login successful:', result),
        error: (error) => console.error('🎭 Auth Service - Mock login error:', error),
      });
    } else {
      console.log('🔐 Auth Service - Starting OIDC login');
      this.oidcSecurityService.authorize();
    }
  }

  /**
   * Logs out the user
   */
  logout(): Observable<any> {
    if (this.isLocalDev) {
      console.log('🎭 Auth Service - Starting mock logout');
      return this.mockAuthService.logout();
    } else {
      console.log('🔐 Auth Service - Starting OIDC logout');
      return this.oidcSecurityService.logoff();
    }
  }

  /**
   * Gets the current authentication state
   */
  getAuthenticationResult(): Observable<{
    isAuthenticated: boolean;
    userData: any;
    accessToken: string;
    idToken: string;
  }> {
    if (this.isLocalDev) {
      return this.mockAuthService.getAuthenticationResult();
    } else {
      return this.oidcSecurityService.checkAuth().pipe(
        map((authResult) => ({
          isAuthenticated: authResult.isAuthenticated,
          userData: authResult.userData,
          accessToken: authResult.accessToken,
          idToken: authResult.idToken,
        })),
        shareReplay(1)
      );
    }
  }

  /**
   * Gets the access token for API calls
   */
  getAccessToken(): Observable<string> {
    return this.isLocalDev ? this.mockAuthService.getAccessToken() : this.oidcSecurityService.getAccessToken();
  }

  /**
   * Gets the ID token
   */
  getIdToken(): Observable<string> {
    return this.isLocalDev ? this.mockAuthService.getIdToken() : this.oidcSecurityService.getIdToken();
  }

  /**
   * Gets user data/claims
   */
  getUserData(): Observable<any> {
    if (this.isLocalDev) {
      return this.mockAuthService.userData$;
    } else {
      return this.oidcSecurityService.getUserData();
    }
  }

  /**
   * Gets user claims from ID token
   */
  getUserClaims(): Observable<any> {
    if (this.isLocalDev) {
      return this.mockAuthService.userData$;
    }

    return this.idToken$.pipe(
      switchMap((token) => {
        if (token) {
          // Decode JWT payload (simple base64 decode - for display purposes only)
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return of(payload);
          } catch (error) {
            console.error('Error decoding token:', error);
            return of(null);
          }
        }
        return of(null);
      })
    );
  }

  /**
   * Checks if user has a specific role or claim
   */
  hasRole(role: string): Observable<boolean> {
    return this.getUserClaims().pipe(
      map((claims) => {
        if (!claims) return false;
        const roles = claims['cognito:groups'] || claims.roles || [];
        return Array.isArray(roles) ? roles.includes(role) : false;
      })
    );
  }

  /**
   * Gets user email from claims
   */
  getUserEmail(): Observable<string | null> {
    return this.getUserClaims().pipe(map((claims) => claims?.email || null));
  }

  /**
   * Gets user name from claims
   */
  getUserName(): Observable<string | null> {
    return this.getUserClaims().pipe(
      map((claims) => {
        if (claims) {
          return (
            claims.name ||
            `${claims.given_name || ''} ${claims.family_name || ''}`.trim() ||
            claims.preferred_username ||
            claims.email ||
            null
          );
        }
        return null;
      })
    );
  }

  /**
   * Refreshes the token
   */
  refreshToken(): Observable<any> {
    if (this.isLocalDev) {
      // Mock refresh - just return current auth state
      return this.mockAuthService.getAuthenticationResult();
    }
    return this.oidcSecurityService.forceRefreshSession();
  }

  /**
   * Checks if the current session/token is valid
   */
  checkSessionChanged(): Observable<boolean> {
    if (this.isLocalDev) {
      // Mock - always return false (no session changes)
      return of(false);
    }
    return this.oidcSecurityService.checkSessionChanged$;
  }

  /**
   * Gets the current tenant ID synchronously (useful for immediate access)
   * Returns null if not available or not authenticated
   */
  getCurrentTenantId(): string | null {
    let currentTenantId: string | null = null;

    // Get current value synchronously
    this.tenantId$.pipe(take(1)).subscribe((tenantId: string | null) => {
      currentTenantId = tenantId;
    });

    return currentTenantId;
  }
}
